import React, { useState, useRef, useEffect } from 'react';
import { COSMETIC_SPECIALTIES, getFilteredSpecialties } from '../constants/specialties';

interface MultiSelectSpecialtyProps {
  selectedSpecialties: string[];
  onSelectionChange: (specialties: string[]) => void;
  placeholder?: string;
  label?: string;
}

const MultiSelectSpecialty: React.FC<MultiSelectSpecialtyProps> = ({
  selectedSpecialties,
  onSelectionChange,
  placeholder = 'Search and select specialties...',
  label = 'Specialties',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>(COSMETIC_SPECIALTIES);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filtered = getFilteredSpecialties(searchTerm);
    setFilteredOptions(filtered);

    // Auto-open dropdown when user starts typing
    if (searchTerm.trim().length > 0) {
      setIsDropdownOpen(true);
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSpecialty = (specialty: string) => {
    if (!selectedSpecialties.includes(specialty)) {
      onSelectionChange([...selectedSpecialties, specialty]);
    }
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const handleRemoveSpecialty = (specialtyToRemove: string) => {
    onSelectionChange(selectedSpecialties.filter((s) => s !== specialtyToRemove));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchTerm.trim()) {
      event.preventDefault();
      // If there's an exact match in filtered options, use it
      const exactMatch = filteredOptions.find(
        (option) => option.toLowerCase() === searchTerm.toLowerCase()
      );

      if (exactMatch) {
        handleAddSpecialty(exactMatch);
      } else if (filteredOptions.length > 0) {
        // Use the first filtered option
        handleAddSpecialty(filteredOptions[0]);
      } else {
        // Add as custom specialty
        handleAddSpecialty(searchTerm.trim());
      }
    } else if (event.key === 'Backspace' && !searchTerm && selectedSpecialties.length > 0) {
      // Remove last selected specialty when backspace is pressed with empty search
      handleRemoveSpecialty(selectedSpecialties[selectedSpecialties.length - 1]);
    }
  };

  const handleInputFocus = () => {
    // Show all options when focusing on empty input, show filtered when there's text
    if (searchTerm.trim().length === 0) {
      setFilteredOptions(COSMETIC_SPECIALTIES);
    }
    setIsDropdownOpen(true);
  };

  return (
    <div className="flex flex-col gap-1 w-full relative" ref={dropdownRef}>
      <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]">{label}</label>

      <div className="relative">
        {/* Selected specialties tags */}
        {selectedSpecialties.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedSpecialties.map((specialty, index) => (
              <div
                key={index}
                className="bg-[#c6a664] text-white px-3 py-1 rounded-full text-sm flex items-center gap-2"
              >
                {specialty}
                <button
                  type="button"
                  onClick={() => handleRemoveSpecialty(specialty)}
                  className="text-white hover:text-gray-200 focus:outline-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472] focus:outline-none focus:border-[#c6a664]"
        />

        {/* Dropdown */}
        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border border-[#eaeaea] border-t-0 max-h-60 overflow-y-auto z-50 shadow-lg">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((specialty, index) => (
                <div
                  key={index}
                  onClick={() => handleAddSpecialty(specialty)}
                  className={`px-6 py-3 cursor-pointer font-['Ovo',_serif] text-[16px] border-b border-[#f0f0f0] last:border-b-0 transition-colors duration-150 ${
                    selectedSpecialties.includes(specialty)
                      ? 'bg-[#c6a664] text-white hover:bg-[#b8975a]'
                      : 'text-[#1f1f1f] hover:bg-[#c6a664] hover:bg-opacity-10 hover:text-[#c6a664]'
                  }`}
                >
                  {specialty}
                  {selectedSpecialties.includes(specialty) && (
                    <span className="float-right">✓</span>
                  )}
                </div>
              ))
            ) : searchTerm.trim() ? (
              <div
                onClick={() => handleAddSpecialty(searchTerm.trim())}
                className="px-6 py-3 cursor-pointer hover:bg-[#c6a664] hover:bg-opacity-10 hover:text-[#c6a664] font-['Ovo',_serif] text-[16px] text-[#c6a664] transition-colors duration-150"
              >
                Add "{searchTerm.trim()}" as custom specialty
              </div>
            ) : (
              <div className="px-6 py-3 font-['Ovo',_serif] text-[16px] text-[#767472]">
                No specialties found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dropdown arrow indicator - only show when not typing */}
      {!searchTerm && (
        <div className="absolute right-5 top-[calc(100%-28px)] transform -translate-y-1/2 pointer-events-none">
          <div className="size-6">
            <svg
              className={`w-full h-full transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="#767472"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Search indicator when typing */}
      {searchTerm && (
        <div className="absolute right-5 top-[calc(100%-28px)] transform -translate-y-1/2 pointer-events-none">
          <div className="size-6 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-[#c6a664]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectSpecialty;
